import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { authAPI } from "../../services/authAPI"
import { useAuth } from "../../hooks/useAuth"

const DeleteAccountModal = ({ isOpen, onClose, onConfirmDelete, onSuspendInstead, hasPassword = true }) => {
  const [selectedReason, setSelectedReason] = useState("")
  const [otherReason, setOtherReason] = useState("")
  const [password, setPassword] = useState("")
  const [showReasonSection, setShowReasonSection] = useState(false)
  const [showPasswordSection, setShowPasswordSection] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const { t, i18n } = useTranslation()
  const { getUserType } = useAuth()

  // Get user type color
  const getUserTypeColor = () => {
    const userType = getUserType()
    const userTypeConfig = {
      'PET_GUARDIAN': '#8548A8', // Purple
      'VETERINARIAN': '#00D4B3', // Cyan
      'SERVICE_PROVIDER': '#D65745', // Orange
      'SERVICE_PROVIDER': '#D65745', // Orange
      'CLINIC_MODERATOR': '#2A8AD2', // Blue
    }
    return userTypeConfig[userType] || '#8548A8' // Default to purple
  }

  // Helper function to darken color for hover
  const darkenColor = (color, percent) => {
    const num = parseInt(color.replace("#", ""), 16)
    const amt = Math.round(2.55 * percent)
    const R = Math.max(0, Math.min(255, (num >> 16) + amt))
    const G = Math.max(0, Math.min(255, ((num >> 8) & 0x00FF) + amt))
    const B = Math.max(0, Math.min(255, (num & 0x0000FF) + amt))
    return "#" + (0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1)
  }

  const userTypeColor = getUserTypeColor()
  const hoverColor = darkenColor(userTypeColor, -15)

  const reasons = useMemo(
    () => [
      { value: "created_new", label: t("accountManagement.modals.delete.reasons.created_new") },
      { value: "privacy_concerns", label: t("accountManagement.modals.delete.reasons.privacy_concerns") },
      { value: "not_using", label: t("accountManagement.modals.delete.reasons.not_using") },
      { value: "other", label: t("accountManagement.modals.delete.reasons.other") },
    ],
    [t]
  )
  const getStoredUser = () => {
    try {
      const stored = localStorage.getItem("user_data")
      return stored ? JSON.parse(stored) : {}
    } catch {
      return {}
    }
  }

  const handleConfirmDelete = async () => {
    const storedUser = getStoredUser()
    const isSocial = storedUser.has_social_login

    const reasonData = {
      reason: selectedReason,
      otherReason: selectedReason === "other" ? otherReason : "",
      password: hasPassword ? password : null, // Send null for OAuth users
    }

    setIsLoading(true)
    try {
      await authAPI.deleteAccount({
        reason: reasonData.reason,
        password: reasonData.password,
        has_social_login: isSocial
      })

      // Call the original onConfirmDelete callback if provided
      if (onConfirmDelete) {
        onConfirmDelete(reasonData)
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || t("accountManagement.modals.delete.errors.failed")
      alert(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    setSelectedReason("")
    setOtherReason("")
    setPassword("")
    setShowReasonSection(false)
    setShowPasswordSection(false)
    onClose()
  }

  const handleDeleteClick = () => {
    setShowReasonSection(true)
  }

  const handleNextToPassword = () => {
    setShowPasswordSection(true)
  }

  const handleNextToPasswordOrConfirm = () => {
    if (hasPassword) {
      setShowPasswordSection(true) // Go to password step
    } else {
      handleConfirmDelete() // Confirm directly without password
    }
  }

  const handleBackToReason = () => {
    setShowPasswordSection(false)
  }

  if (!isOpen) return null

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 10000,
        padding: "20px",
      }}
      onClick={handleClose}
    >
      <div
        style={{
          background: "#FFFFFF",
          borderRadius: "16px",
          padding: "32px 40px 40px 40px",
          maxWidth: "520px",
          width: "100%",
          boxShadow: "0 25px 50px rgba(0, 0, 0, 0.25)",
          position: "relative",
          maxHeight: "90vh",
          overflowY: "auto",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close X Button */}
        <button
          onClick={handleClose}
          style={{
            position: "absolute",
            top: "20px",
            right: "20px",
            background: "transparent",
            border: "none",
            cursor: "pointer",
            padding: "8px",
            borderRadius: "8px",
            transition: "background 0.2s ease",
          }}
          onMouseEnter={(e) => {
            e.target.style.background = "#F9FAFB"
          }}
          onMouseLeave={(e) => {
            e.target.style.background = "transparent"
          }}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#6B7280"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        {/* Modal Content */}
        <div style={{ marginTop: "20px" }} dir={i18n.dir()}>
          <h2
            style={{
              fontFamily: '"Red Hat Display", sans-serif',
              fontSize: "24px",
              fontWeight: 600,
              color: "#303E4E",
              margin: "0 0 16px 0",
              textAlign: "center",
            }}
          >
            {t("accountManagement.modals.delete.title")}
          </h2>

          <p
            style={{
              fontFamily: '"Red Hat Display", sans-serif',
              fontSize: "16px",
              color: "#6B7280",
              lineHeight: "1.6",
              margin: "0 0 24px 0",
              textAlign: "center",
            }}
          >
            {t("accountManagement.modals.delete.description")}
          </p>

          {/* Action Choice Buttons */}
          <div
            style={{
              display: "flex",
              gap: "16px",
              justifyContent: "center",
              marginBottom: "32px",
            }}
          >
            <button
              onClick={onSuspendInstead}
              style={{
                padding: "12px 24px",
                background: "#9CA3AF",
                color: "#FFFFFF",
                border: "none",
                borderRadius: "12px",
                fontFamily: '"Red Hat Display", sans-serif',
                fontSize: "16px",
                fontWeight: 600,
                cursor: "pointer",
                transition: "all 0.2s ease",
                minWidth: "140px",
              }}
              onMouseEnter={(e) => {
                e.target.style.background = "#6B7280"
                e.target.style.transform = "translateY(-1px)"
              }}
              onMouseLeave={(e) => {
                e.target.style.background = "#9CA3AF"
                e.target.style.transform = "translateY(0)"
              }}
            >
              {t("accountManagement.modals.delete.actions.suspend")}
            </button>

            <button
              onClick={handleDeleteClick}
              style={{
                padding: "12px 24px",
                background: userTypeColor,
                color: "#FFFFFF",
                border: "none",
                borderRadius: "12px",
                fontFamily: '"Red Hat Display", sans-serif',
                fontSize: "16px",
                fontWeight: 600,
                cursor: "pointer",
                transition: "all 0.2s ease",
                minWidth: "140px",
              }}
              onMouseEnter={(e) => {
                e.target.style.background = hoverColor
                e.target.style.transform = "translateY(-1px)"
              }}
              onMouseLeave={(e) => {
                e.target.style.background = userTypeColor
                e.target.style.transform = "translateY(0)"
              }}
            >
              {t("accountManagement.modals.delete.actions.delete")}
            </button>
          </div>

          {/* Reason Selection - Only show when user clicks Delete Account AND password section is not shown */}
          {showReasonSection && !showPasswordSection && (
            <div
              style={{
                animation: "fadeIn 0.3s ease-in-out",
              }}
            >
              <div style={{ textAlign: "left" }}>
                <h3
                  style={{
                    fontFamily: '"Red Hat Display", sans-serif',
                    fontSize: "16px",
                    fontWeight: 500,
                    color: "#303E4E",
                    margin: "0 0 16px 0",
                  }}
                >
                  {t("accountManagement.modals.delete.reasonPrompt")}
                </h3>

                {/* Reason Options */}
                <div style={{ marginBottom: "20px" }}>
                  {reasons.map((reason) => (
                    <label
                      key={reason.value}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        marginBottom: "12px",
                        cursor: "pointer",
                        fontFamily: '"Red Hat Display", sans-serif',
                        fontSize: "14px",
                        color: "#374151",
                      }}
                    >
                      <input
                        type="radio"
                        name="deleteReason"
                        value={reason.value}
                        checked={selectedReason === reason.value}
                        onChange={(e) => setSelectedReason(e.target.value)}
                        style={{
                          width: "16px",
                          height: "16px",
                          accentColor: userTypeColor,
                        }}
                      />
                      {reason.label}
                    </label>
                  ))}
                </div>

                {/* Other Reason Text Area */}
                {selectedReason === "other" && (
                  <textarea
                    value={otherReason}
                    onChange={(e) => setOtherReason(e.target.value)}
                    placeholder={t("accountManagement.modals.delete.otherPlaceholder")}
                    style={{
                      width: "100%",
                      height: "80px",
                      padding: "12px",
                      border: "1.5px solid #E5E7EB",
                      borderRadius: "8px",
                      fontFamily: '"Red Hat Display", sans-serif',
                      fontSize: "14px",
                      color: "#303E4E",
                      boxSizing: "border-box",
                      outline: "none",
                      resize: "vertical",
                      marginBottom: "20px",
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = userTypeColor
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = "#E5E7EB"
                    }}
                  />
                )}

                {/* Buttons for reason selection step */}
                <div
                  style={{
                    display: "flex",
                    gap: "16px",
                    justifyContent: "center",
                    marginTop: "24px",
                  }}
                >
                  <button
                    onClick={handleClose}
                    style={{
                      padding: "12px 24px",
                      background: "#9CA3AF",
                      color: "#FFFFFF",
                      border: "none",
                      borderRadius: "12px",
                      fontFamily: '"Red Hat Display", sans-serif',
                      fontSize: "16px",
                      fontWeight: 600,
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      minWidth: "100px",
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = "#6B7280"
                      e.target.style.transform = "translateY(-1px)"
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = "#9CA3AF"
                      e.target.style.transform = "translateY(0)"
                    }}
                  >
                    {t("accountManagement.modals.delete.cancel")}
                  </button>

                  <button
                    onClick={handleNextToPasswordOrConfirm}
                    disabled={!selectedReason || (selectedReason === "other" && !otherReason.trim())}
                    style={{
                      padding: "12px 24px",
                      background: !selectedReason || (selectedReason === "other" && !otherReason.trim()) ? "#D1D5DB" : userTypeColor,
                      color: "#FFFFFF",
                      border: "none",
                      borderRadius: "12px",
                      fontFamily: '"Red Hat Display", sans-serif',
                      fontSize: "16px",
                      fontWeight: 600,
                      cursor: !selectedReason || (selectedReason === "other" && !otherReason.trim()) ? "not-allowed" : "pointer",
                      transition: "all 0.2s ease",
                      minWidth: "140px",
                      opacity: !selectedReason || (selectedReason === "other" && !otherReason.trim()) ? 0.6 : 1,
                    }}
                    onMouseEnter={(e) => {
                      if (selectedReason && !(selectedReason === "other" && !otherReason.trim())) {
                        e.target.style.background = hoverColor
                        e.target.style.transform = "translateY(-1px)"
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedReason && !(selectedReason === "other" && !otherReason.trim())) {
                        e.target.style.background = userTypeColor
                        e.target.style.transform = "translateY(0)"
                      }
                    }}
                  >
                    {hasPassword
                      ? (t("accountManagement.modals.delete.next") || "Next")
                      : (isLoading
                        ? (t("accountManagement.modals.delete.confirmLoading") || "Deleting...")
                        : (t("accountManagement.modals.delete.confirm") || "Confirm Deletion")
                      )
                    }
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Password Section - Only show when password section is active */}
          {showPasswordSection && (
            <div
              style={{
                animation: "fadeIn 0.3s ease-in-out",
              }}
            >
              <div style={{ textAlign: "left" }}>
                <p
                  style={{
                    fontFamily: '"Red Hat Display", sans-serif',
                    fontSize: "14px",
                    color: "#6B7280",
                    lineHeight: "1.6",
                    margin: "0 0 24px 0",
                    textAlign: "center",
                  }}
                >
                  {t("accountManagement.modals.delete.passwordPrompt") || "Please enter your password to confirm account deletion."}
                </p>

                {/* Password Input */}
                <div style={{ marginBottom: "20px" }}>
                  <label
                    htmlFor="delete-password"
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontFamily: '"Red Hat Display", sans-serif',
                      fontSize: "14px",
                      color: "#374151",
                      fontWeight: 500,
                    }}
                  >
                    {t("common.password")}
                  </label>
                  <input
                    id="delete-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t("common.enterPassword")}
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      borderRadius: "8px",
                      border: "1px solid #D1D5DB",
                      fontSize: "16px",
                      outline: "none",
                      fontFamily: '"Red Hat Display", sans-serif',
                      boxSizing: "border-box",
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = userTypeColor
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = "#D1D5DB"
                    }}
                  />
                </div>

                {/* Password section buttons */}
                <div
                  style={{
                    display: "flex",
                    gap: "16px",
                    justifyContent: "center",
                    marginTop: "24px",
                  }}
                >
                  <button
                    onClick={handleBackToReason}
                    style={{
                      padding: "12px 24px",
                      background: "#9CA3AF",
                      color: "#FFFFFF",
                      border: "none",
                      borderRadius: "12px",
                      fontFamily: '"Red Hat Display", sans-serif',
                      fontSize: "16px",
                      fontWeight: 600,
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      minWidth: "100px",
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = "#6B7280"
                      e.target.style.transform = "translateY(-1px)"
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = "#9CA3AF"
                      e.target.style.transform = "translateY(0)"
                    }}
                  >
                    {t("accountManagement.modals.delete.back") || "Back"}
                  </button>

                  <button
                    onClick={handleConfirmDelete}
                    disabled={!password.trim() || isLoading}
                    style={{
                      padding: "12px 24px",
                      background: !password.trim() || isLoading ? "#D1D5DB" : "#DC2626",
                      color: "#FFFFFF",
                      border: "none",
                      borderRadius: "12px",
                      fontFamily: '"Red Hat Display", sans-serif',
                      fontSize: "16px",
                      fontWeight: 600,
                      cursor: !password.trim() || isLoading ? "not-allowed" : "pointer",
                      transition: "all 0.2s ease",
                      minWidth: "140px",
                      opacity: !password.trim() || isLoading ? 0.6 : 1,
                    }}
                    onMouseEnter={(e) => {
                      if (password.trim() && !isLoading) {
                        e.target.style.background = "#B91C1C"
                        e.target.style.transform = "translateY(-1px)"
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (password.trim() && !isLoading) {
                        e.target.style.background = "#DC2626"
                        e.target.style.transform = "translateY(0)"
                      }
                    }}
                  >
                    {isLoading
                      ? t("accountManagement.modals.delete.confirmLoading")
                      : t("accountManagement.modals.delete.confirm")}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default DeleteAccountModal

// Add CSS animation
const deleteModalStyle = document.createElement('style')
deleteModalStyle.textContent = `
      @keyframes fadeIn {
        from {
        opacity: 0;
      transform: translateY(10px);
    }
      to {
        opacity: 1;
      transform: translateY(0);
    }
  }
      `
if (typeof document !== 'undefined' && !document.getElementById('delete-modal-animations')) {
  deleteModalStyle.id = 'delete-modal-animations'
  document.head.appendChild(deleteModalStyle)
}
